import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Plane, Globe, Lock, CheckCircle2, Clock3 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

/**
 * Tour Fund dashboard card.
 * - Auto-detects Domestic/International eligibility from backend
 * - Shows Admin-configured fund amount
 * - Withdraw button unlocks ONLY after Admin flips `withdraw_enabled`
 */
export default function TourFundCard() {
  const [state, setState] = useState({ funds: [], enabled: true, loading: true });
  const [wd, setWd] = useState({ open: false, fund: null, wallet: "", busy: false });

  const load = async () => {
    try {
      const { data } = await api.get("/tour/funds");
      setState({ ...data, loading: false });
    } catch (e) {
      setState({ funds: [], enabled: false, loading: false });
    }
  };
  useEffect(() => { load(); }, []);

  const openWd = (fund) => setWd({ open: true, fund, wallet: "", busy: false });
  const submitWd = async () => {
    if (!wd.fund) return;
    setWd((s) => ({ ...s, busy: true }));
    try {
      await api.post("/tour/withdraw", { fund_id: wd.fund.id, wallet_address: wd.wallet.trim() });
      toast.success("Tour fund withdrawal submitted");
      setWd({ open: false, fund: null, wallet: "", busy: false });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setWd((s) => ({ ...s, busy: false }));
    }
  };

  if (state.loading) return null;
  if (!state.enabled) return null;

  const domesticFund = state.funds.find((f) => f.kind === "domestic");
  const internationalFund = state.funds.find((f) => f.kind === "international");

  return (
    <div className="nb-card p-5" data-testid="tour-fund-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Plane size={16} className="text-nb-cyan"/>
          <div className="font-heading font-semibold text-white text-sm">Tour Rewards Fund</div>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-slate-500">Auto-eligibility</span>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <TourTile
          testid="tour-fund-domestic"
          label="Domestic Tour"
          icon={<Plane size={14} />}
          accent="#8AE234"
          fund={domesticFund}
          eligible={state.domestic_eligible}
          notEligibleHint="Reach Leadership OR $2,000 in total holdings"
          onWithdraw={() => openWd(domesticFund)}
        />
        <TourTile
          testid="tour-fund-international"
          label="International Tour"
          icon={<Globe size={14} />}
          accent="#00E5FF"
          fund={internationalFund}
          eligible={state.international_eligible}
          notEligibleHint="Become Diamond Shareholder OR any Shareholder + $20k team business"
          onWithdraw={() => openWd(internationalFund)}
        />
      </div>

      <Dialog open={wd.open} onOpenChange={(o) => !o && setWd({ open: false, fund: null, wallet: "", busy: false })}>
        <DialogContent data-testid="tour-fund-withdraw-dialog">
          <DialogHeader>
            <DialogTitle>Withdraw {wd.fund?.kind === "international" ? "International" : "Domestic"} Tour Fund</DialogTitle>
            <DialogDescription>${Number(wd.fund?.amount || 0).toFixed(2)} · BEP20 USDT payout · Company approves within 24h.</DialogDescription>
          </DialogHeader>
          <label className="text-xs uppercase tracking-widest text-slate-500">BEP20 Wallet Address</label>
          <input
            data-testid="tour-fund-wallet-input"
            className="nb-input w-full"
            placeholder="0x..."
            value={wd.wallet}
            onChange={(e) => setWd((s) => ({ ...s, wallet: e.target.value }))}
          />
          <DialogFooter>
            <button className="nb-btn-ghost text-xs" onClick={() => setWd({ open: false, fund: null, wallet: "", busy: false })} disabled={wd.busy}>Cancel</button>
            <button
              data-testid="tour-fund-submit-btn"
              className="nb-btn-primary text-xs"
              disabled={wd.busy || !wd.wallet.trim()}
              onClick={submitWd}
            >{wd.busy ? "Submitting..." : "Submit withdrawal"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TourTile({ testid, label, icon, accent, fund, eligible, notEligibleHint, onWithdraw }) {
  const amount = Number(fund?.amount || 0);
  const status = fund?.status;
  const withdrawEnabled = fund?.withdraw_enabled;

  let stateBadge, stateBadgeColor, cta;
  if (!eligible) {
    stateBadge = "Locked — not eligible yet";
    stateBadgeColor = "text-slate-500";
    cta = <div className="text-[11px] text-slate-500 mt-2 leading-relaxed">{notEligibleHint}</div>;
  } else if (!fund) {
    stateBadge = "Eligibility detecting...";
    stateBadgeColor = "text-slate-400";
  } else if (status === "paid") {
    stateBadge = "Paid out";
    stateBadgeColor = "text-[#8AE234]";
    cta = <div className="mt-2 flex items-center gap-1 text-[11px] text-[#8AE234]"><CheckCircle2 size={12}/> Received on {String(fund.paid_at || "").slice(0, 10)}</div>;
  } else if (status === "withdraw_requested") {
    stateBadge = "Withdrawal pending — Company processing";
    stateBadgeColor = "text-[#EAB308]";
    cta = <div className="mt-2 flex items-center gap-1 text-[11px] text-[#EAB308]"><Clock3 size={12}/> Awaiting payout</div>;
  } else if (withdrawEnabled) {
    stateBadge = "Ready to withdraw";
    stateBadgeColor = "text-[#8AE234]";
    cta = <button data-testid={`${testid}-withdraw-btn`} className="nb-btn-primary text-xs mt-3 w-full" onClick={onWithdraw}>Withdraw ${amount.toFixed(0)}</button>;
  } else {
    stateBadge = "Awaiting Company approval";
    stateBadgeColor = "text-[#EAB308]";
    cta = (
      <div className="mt-2 flex items-center gap-1 text-[11px] text-[#EAB308]">
        <Lock size={12}/> Company will unlock your withdraw switch soon.
      </div>
    );
  }

  return (
    <div data-testid={testid} className="rounded-xl border p-4" style={{borderColor: `${accent}33`, background: `${accent}0f`}}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest" style={{color: accent}}>
          {icon} {label}
        </div>
        {eligible && <CheckCircle2 size={14} className="text-[#8AE234]"/>}
      </div>
      <div className="mt-2 font-mono text-3xl font-bold" style={{color: eligible ? accent : "#64748B"}}>
        ${amount.toFixed(0)}
      </div>
      <div className={`text-[11px] uppercase tracking-widest mt-1 ${stateBadgeColor}`}>{stateBadge}</div>
      {cta}
    </div>
  );
}
